import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { toast } from "sonner";
import { ArrowLeft, Eye, KeyRound, UserX } from "lucide-react";
import { http } from "@/lib/api";
import { fetchUsers, setUserRole, fetchStats, fetchLinkStats } from "@/lib/api";
import InvitePanel from "./InvitePanel";
import ReportPanel from "./ReportPanel";
import InsightsPanel from "./InsightsPanel";
import InterestedList from "./InterestedList";

const LABEL = { admin: "Administrator", agent: "Agent" };

const ago = (iso) => {
  if (!iso) return "fără activitate";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "activ azi" : d === 1 ? "activ ieri" : `activ acum ${d} zile`;
};

const g = (n, one, many) => (n === 1 ? one : many);

const LinkStats = ({ l, id }) => (
  <div className={`user-stats link-stats ${l?.clients ? "is-active" : ""}`} data-testid={`user-link-stats-${id}`}>
    <span title="Clienți unici care au deschis linkul"><b>{l?.clients || 0}</b> clienți</span>
    <span title="Total deschideri ale linkului"><b>{l?.views || 0}</b> vizite</span>
    <span title="PDF-uri descărcate de clienți"><b>{l?.pdfs || 0}</b> PDF</span>
    <span title="Comenzi trimise pe WhatsApp de clienți" data-testid={`user-orders-${id}`}><b>{l?.orders || 0}</b> comenzi</span>
    <span className="is-offer" title="Oferte rapide trimise / deschise de saloane / comandate" data-testid={`user-offers-${id}`}>
      oferte <b>{l?.offers_sent || 0}</b> {g(l?.offers_sent, "trimisă", "trimise")} · <b>{l?.offers_opened || 0}</b> {g(l?.offers_opened, "deschisă", "deschise")} · <b>{l?.offers_ordered || 0}</b> {g(l?.offers_ordered, "comandată", "comandate")}
    </span>
    <small>{l?.last ? `${ago(l.last).replace("activ", "deschis")}` : "link nedeschis"}{l?.clients30 ? ` · ${l.clients30} clienți în 30 zile` : ""}</small>
  </div>
);

const Stats = ({ s, id }) => (
  <div className={`user-stats ${s?.recent ? "is-active" : ""}`} data-testid={`user-stats-${id}`}>
    <span title="PDF-uri descărcate"><b>{s?.pdf || 0}</b> PDF</span>
    <span title="Pachete ZIP descărcate"><b>{s?.zip || 0}</b> ZIP</span>
    <span title="Imagini PNG descărcate"><b>{s?.png || 0}</b> PNG</span>
    <small>{ago(s?.last)}{s?.recent ? ` · ${s.recent} în 30 zile` : ""}</small>
  </div>
);

export default function UsersPage({ me }) {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState({});
  const [links, setLinks] = useState({});
  const load = () => {
    fetchUsers().then(setData).catch(() => toast.error("Nu am putut încărca agenții"));
    fetchStats().then(setStats).catch(() => {});
    fetchLinkStats().then(setLinks).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const change = async (id, role) => {
    try {
      await setUserRole(id, role);
      toast.success("Rol actualizat");
      load();
    } catch (e) {
      toast.error(e.response?.status === 409 ? "Trebuie să rămână cel puțin un administrator" : "Nu am putut schimba rolul");
    }
  };

  return (
    <div className="st-app">
      <header className="st-top">
        <div className="st-brand">
          <Link to="/" className="st-btn st-btn--ghost" data-testid="back-to-studio"><ArrowLeft size={16} /> Catalog</Link>
          <span className="st-brand-mark">Agenți</span>
        </div>
        <span className="st-user"><UserButton /></span>
      </header>
      <main className="users-main" data-testid="users-page">
        <InvitePanel />
        <ReportPanel />
        <section className="invite-box"><InterestedList showAgent /></section>
        <InsightsPanel users={data?.users || []} />
        <h3 className="users-title">Conturi și activitate</h3>
        <p className="st-hint">Oricine își face cont devine agent: își poate pune doar numele și telefonul pe materialele lui.</p>
        {!data ? (
          <p className="st-hint">Se încarcă…</p>
        ) : (
          <ul className="users-list">
            {[...data.users].sort((a, b) => ((stats[b.user_id]?.recent || 0) - (stats[a.user_id]?.recent || 0))).map((u) => (
              <li key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <div>
                  <b>{u.name || "Fără nume"}</b>
                  <span>{u.email}{u.phone ? ` · ${u.phone}` : ""}</span>
                  {u.share_slug && (
                    <a className="user-preview" href={`/c/${u.share_slug}?preview=1`} target="_blank" rel="noopener noreferrer" data-testid={`user-preview-link-${u.user_id}`}>
                      <Eye size={12} /> Vezi ce vede clientul
                    </a>
                  )}
                  {u.user_id !== me.user_id && (
                    <button type="button" className="user-preview" onClick={() => http.post(`/users/${u.user_id}/login-link`).then((r) => navigator.clipboard.writeText(r.data.url)).then(() => toast.success("Link de logare copiat (valabil 24 de ore, o singură folosire)")).catch(() => toast.error("Nu am putut crea linkul"))} data-testid={`user-login-link-${u.user_id}`}>
                      <KeyRound size={12} /> Copiază link de logare
                    </button>
                  )}
                  {u.user_id !== me.user_id && (
                    <button type="button" className="user-preview user-remove" onClick={() => {
                      if (!window.confirm(`Scoți contul ${u.name || u.email}? Nu se mai poate loga, iar istoricul comenzilor rămâne.`)) return;
                      http.delete(`/users/${u.user_id}`).then(() => { toast.success("Cont scos"); load(); }).catch((e) => toast.error(e.response?.data?.detail || "Nu am putut scoate contul"));
                    }} data-testid={`user-remove-${u.user_id}`}>
                      <UserX size={12} /> Scoate contul
                    </button>
                  )}
                </div>
                <LinkStats l={links[u.user_id]} id={u.user_id} />
                <Stats s={stats[u.user_id]} id={u.user_id} />
                <select
                  value={u.role}
                  disabled={u.user_id === me.user_id}
                  onChange={(e) => change(u.user_id, e.target.value)}
                  data-testid={`user-role-select-${u.user_id}`}
                >
                  {data.roles.map((r) => <option key={r} value={r}>{LABEL[r] || r}</option>)}
                </select>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
